import React, { useState, useEffect, useRef } from 'react';
import {
    Search,
    Users as UsersIcon,
    Eye,
    Power,
    PowerOff,
    LogIn,
    ArrowUpDown
} from 'lucide-react';
import { userApi } from '../services/userApi';
import { useUIStore } from '../stores/ui';
import PageHeader from '../components/ui/PageHeader';
import Badge from '../components/ui/Badge';
import Card from '../components/ui/Card';
import { UserDetailModal } from '../components/users/UserDetailModal';
import { UserConfirmationModal } from '../components/users/UserConfirmationModal';

function getFlagEmoji(code) {
    if (!code) return null;
    code = code.toUpperCase();
    return code.replace(/./g, char =>
        String.fromCodePoint(127397 + char.charCodeAt())
    );
}

const Pager = ({ page, pages, onGo }) => {
    const items = [];
    const push = (k, label = k, disabled = false, active = false) => {
        items.push(
            <button
                key={k + label}
                disabled={disabled}
                onClick={() => !disabled && onGo(k)}
                className={`px-2.5 py-1 text-sm border rounded dark:border-slate-700 ${
                    active 
                        ? 'bg-gray-600 text-white border-gray-600 dark:bg-slate-700 dark:text-slate-100 dark:border-slate-600' 
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800'
                } disabled:opacity-50`}
            >
                {label}
            </button>
        );
    };

    push(1, '<<', page === 1);
    push(page - 1, '<', page === 1);

    const windowSize = 2;
    const start = Math.max(1, page - windowSize);
    const end = Math.min(pages, page + windowSize);

    if (start > 1) {
        push(1, 1, false, page === 1);
        if (start > 2) items.push(<span key="l-ellipsis" className="px-1 text-sm dark:text-slate-300">...</span>);
    }

    for (let p = start; p <= end; p++) push(p, p, false, p === page);

    if (end < pages) {
        if (end < pages - 1) items.push(<span key="r-ellipsis" className="px-1 text-sm dark:text-slate-300">...</span>);
        push(pages, pages, false, page === pages);
    }

    push(page + 1, '>', page === pages);
    push(pages, '>>', page === pages);

    return (
        <div className="flex items-center gap-1">{items}</div>
    );
};

export default function UsersPage() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [isOnboardedFilter, setIsOnboardedFilter] = useState(false);
    const [countryCodeFilter, setCountryCodeFilter] = useState('');
    const [sortBy, setSortBy] = useState('');
    const [sort, setSort] = useState('ASC');
    const [selectedUser, setSelectedUser] = useState(null);
    const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, total_pages: 1 });
    const [error, setError] = useState(null);
    const [confirmModal, setConfirmModal] = useState({
        isOpen: false,
        user: null,
        action: null
    });
    
    const searchRef = useRef(null);
    const debounceTimerRef = useRef(null);
    const addToast = useUIStore(s => s.addToast);

    const loadPage = async (page = 1, search = searchTerm, status = statusFilter, isOnboarded = isOnboardedFilter, countryCode = countryCodeFilter, sortByParam = sortBy, sortParam = sort) => {
        setLoading(true);
        setError(null);
        try {
            const normStatus = status === '' ? undefined : Number(status);
            const params = {
                page,
                limit: pagination.limit,
                search: search.trim(),
                status: normStatus
            };

            if (isOnboarded) {
                params.is_onboarded = true;
            }

            if (countryCode.trim()) {
                params.country_code = countryCode.trim();
            }

            if (sortByParam) {
                params.sortBy = sortByParam;
                params.sort = sortParam;
            }

            const result = await userApi.list(params);
            setUsers(result.data || []);
            setPagination({
                page: result.pagination?.page ?? page,
                limit: result.pagination?.limit ?? pagination.limit,
                total: result.pagination?.total ?? 0,
                total_pages: result.pagination?.pages ?? result.pagination?.total_pages ?? 1,
            });
        } catch (e) {
            setError(e.message);
            setUsers([]);
            addToast({ type: 'error', message: `Failed to load users: ${e.message}` });
        } finally {
            setLoading(false);
        }
    };

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        
        debounceTimerRef.current = setTimeout(() => {
            loadPage(1, value, statusFilter, isOnboardedFilter, countryCodeFilter, sortBy, sort);
        }, 300);
    };

    const handleSort = (column) => {
        const validSortColumns = ['status', 'is_onboarded', 'lead_score', 'created_at', 'last_login_at'];
        if (!validSortColumns.includes(column)) return;

        let newSort = 'ASC';
        if (sortBy === column) {
            newSort = sort === 'ASC' ? 'DESC' : 'ASC';
        }

        setSortBy(column);
        setSort(newSort);
        loadPage(1, searchTerm, statusFilter, isOnboardedFilter, countryCodeFilter, column, newSort);
    };

    useEffect(() => {
        loadPage(1, '', statusFilter, isOnboardedFilter, countryCodeFilter, sortBy, sort);
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleStatusConfirm = () => {
        if (confirmModal.user && confirmModal.action !== null) {
            const isEnable = confirmModal.action === 1;
            userApi.toggleStatus(confirmModal.user.id, confirmModal.action)
                .then(() => {
                    addToast({ type: 'success', message: `User ${confirmModal.user.email} has been ${isEnable ? 'enabled' : 'disabled'}` });
                    loadPage(pagination.page, searchTerm, statusFilter, isOnboardedFilter, countryCodeFilter, sortBy, sort);
                })
                .catch(err => {
                    setError(err.message);
                    addToast({ type: 'error', message: `Failed to update user status: ${err.message}` });
                });
        }
    };

    const SortableHeader = ({ column, label, validColumns }) => {
        const isValidSort = validColumns.includes(column);
        const isActive = sortBy === column;

        return (
            <th
                className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-slate-300 dark:bg-slate-900 ${
                    isValidSort ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-800' : ''
                }`}
                onClick={() => isValidSort && handleSort(column)}
            >
                <div className="flex items-center gap-1">
                    {label}
                    {isValidSort && (
                        <ArrowUpDown
                            className={`h-3 w-3 ${
                                isActive ? 'text-gray-700 dark:text-slate-100' : 'text-gray-400 dark:text-slate-500'
                            }`}
                        />
                    )}
                    {isActive && (
                        <span className="text-xs font-normal text-gray-600 dark:text-slate-300">
                            ({sort})
                        </span>
                    )}
                </div>
            </th>
        );
    };

    const validSortColumns = ['status', 'is_onboarded', 'lead_score', 'created_at', 'last_login_at'];

    return (
        <div className="space-y-6">
            <PageHeader 
                title="Users" 
                description="Manage user accounts" 
                icon={UsersIcon}
            />

            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 rounded p-3 text-sm dark:bg-red-900/30 dark:border-red-800 dark:text-red-300">
                    {error}
                </div>
            )}

            <Card className="p-4 bg-white dark:bg-slate-900 dark:border-slate-700 border border-gray-200">
                <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
                    <div className="flex flex-col sm:flex-row gap-4 flex-wrap">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Keyword</label>
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-slate-500"/>
                                <input
                                    ref={searchRef}
                                    type="text"
                                    placeholder="Search users..."
                                    value={searchTerm}
                                    onChange={handleSearchChange}
                                    className="w-full sm:w-80 pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-gray-500 focus:border-gray-500 text-sm dark:bg-slate-900 dark:text-slate-100 dark:border-slate-700 dark:focus:ring-slate-500"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Status</label>
                            <select
                                value={statusFilter}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    setStatusFilter(v);
                                    loadPage(1, searchTerm, v, isOnboardedFilter, countryCodeFilter, sortBy, sort);
                                }}
                                className="text-sm border border-gray-300 rounded px-3 py-2 bg-white min-w-[120px] dark:bg-slate-900 dark:text-slate-100 dark:border-slate-700"
                            >
                                <option value="">All</option>
                                <option value="1">Active</option>
                                <option value="0">Disabled</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Country Code</label>
                            <input
                                type="text"
                                placeholder="e.g., TN, US"
                                value={countryCodeFilter}
                                onChange={(e) => setCountryCodeFilter(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        loadPage(1, searchTerm, statusFilter, isOnboardedFilter, countryCodeFilter, sortBy, sort);
                                    }
                                }}
                                className="w-full sm:w-32 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-gray-500 focus:border-gray-500 text-sm dark:bg-slate-900 dark:text-slate-100 dark:border-slate-700"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Onboarded</label>
                            <div className="flex items-center h-[38px]">
                                <label className="flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={isOnboardedFilter}
                                        onChange={(e) => {
                                            const checked = e.target.checked;
                                            setIsOnboardedFilter(checked);
                                            loadPage(1, searchTerm, statusFilter, checked, countryCodeFilter, sortBy, sort);
                                        }}
                                        className="w-4 h-4 text-gray-600 border-gray-300 rounded focus:ring-gray-500 dark:bg-slate-900 dark:border-slate-700 dark:checked:bg-slate-600"
                                    />
                                    <span className="ml-2 text-sm text-gray-700 dark:text-slate-300">Only onboarded</span>
                                </label>
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end">
                        <Pager
                            page={pagination.page}
                            pages={pagination.total_pages}
                            onGo={(p) => loadPage(p, searchTerm, statusFilter, isOnboardedFilter, countryCodeFilter, sortBy, sort)}
                        />
                    </div>
                </div>
            </Card>

            <div className="bg-white dark:bg-slate-900 rounded-lg border border-gray-200 dark:border-slate-700 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-700">
                        <thead className="bg-gray-50 dark:bg-slate-900">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-slate-300 dark:bg-slate-900">
                                    Name
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-slate-300 dark:bg-slate-900">
                                    Phone
                                </th>
                                <SortableHeader column="is_onboarded" label="Onboarded" validColumns={validSortColumns} />
                                <SortableHeader column="lead_score" label="Lead Score" validColumns={validSortColumns} />
                                <SortableHeader column="status" label="Status" validColumns={validSortColumns} />
                                <SortableHeader column="created_at" label="Created" validColumns={validSortColumns} />
                                <SortableHeader column="last_login_at" label="Last Login" validColumns={validSortColumns} />
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-slate-300 dark:bg-slate-900">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-slate-900 divide-y divide-gray-200 dark:divide-slate-700">
                            {loading ? (
                                <tr>
                                    <td colSpan="8" className="px-6 py-12 text-center text-sm text-gray-500 dark:text-slate-400">
                                        Loading...
                                    </td>
                                </tr>
                            ) : users.length === 0 ? (
                                <tr>
                                    <td colSpan="8" className="px-6 py-12 text-center text-sm text-gray-500 dark:text-slate-400">
                                        No users found
                                    </td>
                                </tr>
                            ) : (
                                users.map((row) => (
                                    <tr key={row.id} className="hover:bg-gray-50 dark:hover:bg-slate-800">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center space-x-3">
                                                <div className="h-8 w-8 rounded-full bg-gray-100 dark:bg-slate-700 flex items-center justify-center">
                                                    {row.photo != null ? (
                                                        <img src={row.photo} alt="User" className="h-8 w-8 rounded-full object-cover" onError={(e) => e.target.src = 'https://placehold.co/600x400/transparent/gray?text='+  row.firstname?.[0]?.toUpperCase()+(row.lastname?.[0]??row.firstname?.[1]).toUpperCase()}/>
                                                    ) : (
                                                        <span className="text-sm font-medium text-gray-600 dark:text-slate-300">
                                                            {row.firstname?.[0]?.toUpperCase()}{row.lastname?.[0]?.toUpperCase()}
                                                        </span>
                                                    )}
                                                </div>
                                                <div>
                                                    <div className="text-sm font-medium text-gray-900 dark:text-slate-100">{row.firstname} {row.lastname} {row.last_country_code && (
                                                       <> {getFlagEmoji(row.last_country_code)}</>
                                                    )}</div>
                                                    <div className="text-sm text-gray-500 dark:text-slate-400">{row.email}</div>

                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-slate-300">
                                            {row.tel || 'N/A'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                            {row.is_onboarded ? (
                                                <Badge variant="info">Yes</Badge>
                                            ) : (
                                                <Badge variant="secondary">No</Badge>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-slate-300">
                                            {row.lead_score ?? 'N/A'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <Badge variant={row.status === 1 ? 'success' : 'danger'}>
                                                {row.status === 1 ? 'Active' : 'Disabled'}
                                            </Badge>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-slate-300">
                                            {row.created_at ? new Date(row.created_at).toLocaleDateString() : '-'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-slate-300">
                                            {row.last_login_at ? new Date(row.last_login_at).toLocaleDateString() : '-'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                            <div className="flex items-center space-x-2">
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        userApi.detail(row.id).then(setSelectedUser).catch(err => {
                                                            setError(err.message);
                                                            addToast({ type: 'error', message: `Failed to load user details: ${err.message}` });
                                                        });
                                                    }}
                                                    className="text-gray-600 hover:text-gray-800 dark:text-slate-400 dark:hover:text-slate-200 p-1"
                                                    title="View"
                                                >
                                                    <Eye className="h-4 w-4"/>
                                                </button>
                                                <button
                                                    onClick={(e)=>{
                                                        e.stopPropagation();
                                                        const newStatus = row.status === 1 ? 0 : 1;
                                                        setConfirmModal({
                                                            isOpen: true,
                                                            user: row,
                                                            action: newStatus
                                                        });
                                                    }}
                                                    className={`p-1 ${row.status===1? 'text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300':'text-green-500 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300'}`}
                                                    title={row.status===1? 'Disable':'Enable'}
                                                >
                                                    {row.status===1? <PowerOff className="h-4 w-4"/> : <Power className="h-4 w-4"/>}
                                                </button>

                                                <button
                                                    onClick={async (e)=>{
                                                        e.stopPropagation();
                                                        // open the tab right away so the browser does not block it as a popup
                                                        const tab = window.open('', '_blank');
                                                        try {
                                                            tab.location.href = await userApi.autologinUrl(row.id);
                                                        } catch (err) {
                                                            tab?.close();
                                                            addToast({ type: 'error', message: err.message || 'Auto login failed' });
                                                        }
                                                    }}
                                                    className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 p-1"
                                                    title="Auto Login"
                                                >
                                                    <LogIn className="h-4 w-4"/>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="flex justify-center">
                <Pager
                    page={pagination.page}
                    pages={pagination.total_pages}
                    onGo={(p) => loadPage(p, searchTerm, statusFilter, isOnboardedFilter, countryCodeFilter, sortBy, sort)}
                />
            </div>

            <UserDetailModal
                user={selectedUser}
                open={!!selectedUser}
                onClose={() => setSelectedUser(null)}
                onStatusToggle={(id, status) => {
                    userApi.toggleStatus(id, status)
                        .then(() => {
                            addToast({ type: 'success', message: `User status updated successfully` });
                            loadPage(pagination.page, searchTerm, statusFilter, isOnboardedFilter, countryCodeFilter, sortBy, sort);
                            if (selectedUser && selectedUser.id === id) {
                                setSelectedUser({ ...selectedUser, status });
                            }
                        })
                        .catch(err => {
                            setError(err.message);
                            addToast({ type: 'error', message: `Failed to update user status: ${err.message}` });
                        });
                }}
            />

            <UserConfirmationModal
                open={confirmModal.isOpen}
                onClose={() => setConfirmModal({ isOpen: false, user: null, action: null })}
                onConfirm={handleStatusConfirm}
                user={confirmModal.user}
                action={confirmModal.action === 1 ? 'enable' : 'disable'}
                loading={false}
            />
        </div>
    );
}
